import React, { useState, useRef, useMemo, useCallback } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import styled from 'styled-components'
import useMediaQuery from 'hooks/useMediaQuery'
import * as _ from 'styles/helpers'

// Register custom camera icon for Quill toolbar
const icons = Quill.import('ui/icons')
icons['camera'] = '<svg viewBox="0 0 18 18"><circle class="ql-fill" cx="9" cy="9" r="3"/><rect class="ql-stroke" height="12" width="12" x="3" y="3" rx="2" ry="2"/><line class="ql-stroke" x1="12" x2="12" y1="6" y2="6"/></svg>'

const RTEComponent = ({ input, setInput }) => {
	const quillRef = useRef(null)
	const isMobile = useMediaQuery(_.MQ.small_and_below)

	// Custom camera handler for mobile
	const cameraHandler = useCallback(() => {
		const input = document.createElement('input')
		input.setAttribute('type', 'file')
		input.setAttribute('accept', 'image/*')
		input.setAttribute('capture', 'environment') // Use rear camera

		input.click()

		input.onchange = async () => {
			const file = input.files[0]
			if (file) {
				const reader = new FileReader()
				reader.onload = e => {
					const quill = quillRef.current.getEditor()
					const range = quill.getSelection(true)
					quill.insertEmbed(range.index, 'image', e.target.result)
					quill.setSelection(range.index + 1)
				}
				reader.readAsDataURL(file)
			}
		}
	}, [])

	// Custom file upload handler for mobile
	const fileUploadHandler = useCallback(() => {
		const input = document.createElement('input')
		input.setAttribute('type', 'file')
		input.setAttribute('accept', 'image/*')
		// No capture attribute - allows file selection from gallery

		input.click()

		input.onchange = async () => {
			const file = input.files[0]
			if (file) {
				const reader = new FileReader()
				reader.onload = e => {
					const quill = quillRef.current.getEditor()
					const range = quill.getSelection(true)
					quill.insertEmbed(range.index, 'image', e.target.result)
					quill.setSelection(range.index + 1)
				}
				reader.readAsDataURL(file)
			}
		}
	}, [])

	// Desktop image handler
	const imageHandler = useCallback(() => {
		const input = document.createElement('input')
		input.setAttribute('type', 'file')
		input.setAttribute('accept', 'image/*')

		input.click()

		input.onchange = async () => {
			const file = input.files[0]
			if (file) {
				const reader = new FileReader()
				reader.onload = e => {
					const quill = quillRef.current.getEditor()
					const range = quill.getSelection(true)
					quill.insertEmbed(range.index, 'image', e.target.result)
					quill.setSelection(range.index + 1)
				}
				reader.readAsDataURL(file)
			}
		}
	}, [])

	const modules = useMemo(
		() => ({
			toolbar: {
				container: isMobile
					? [
							['camera', 'image', 'link'], // Mobile: only camera, file upload, and link
					  ]
					: [
							[{ header: [1, 2, 3, 4, 5, 6, false] }],
							['bold', 'italic', 'underline', 'strike'],
							[
								{
									color: ['#F00', 'yellow', '#0F0', 'green', '#00F', '#000', 'grey'],
								},
							],
							[{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
							['link', 'image', 'video'],
							['clean'],
					  ],
				handlers: isMobile
					? {
							camera: cameraHandler,
							image: fileUploadHandler,
					  }
					: {
							image: imageHandler,
					  },
			},
		}),
		[isMobile, cameraHandler, fileUploadHandler, imageHandler]
	)

	const formats = [
		'header',
		'bold',
		'italic',
		'underline',
		'strike',
		'blockquote',
		'list',
		'bullet',
		'link',
		'indent',
		'image',
		'code-block',
		'color',
	]

	return (
		<StyledRTEWrapper isMobile={isMobile}>
			<ReactQuill
				ref={quillRef}
				theme="snow"
				placeholder="add note . . ."
				value={input}
				onChange={setInput}
				className="editor-input"
				modules={modules}
				formats={formats}
			/>
		</StyledRTEWrapper>
	)
}

const StyledRTEWrapper = styled.div`
	${props =>
		props.isMobile &&
		`
		.ql-toolbar {
			padding: 4px !important;

			.ql-stroke {
				stroke-width: 1.5px;
			}

			.ql-fill {
				fill: currentColor;
			}

			button {
				width: 28px !important;
				height: 28px !important;
				padding: 4px !important;
				margin: 0 2px !important;

				svg {
					width: 18px !important;
					height: 18px !important;
				}
			}

			.ql-picker {
				font-size: 12px !important;

				&.ql-header {
					width: 80px !important;

					.ql-picker-label {
						padding-left: 6px !important;
						padding-right: 20px !important;

						svg {
							width: 14px !important;
							height: 14px !important;
						}
					}
				}

				&.ql-color,
				&.ql-background {
					width: 28px !important;

					.ql-picker-label {
						padding: 4px !important;

						svg {
							width: 18px !important;
							height: 18px !important;
						}
					}
				}
			}

			.ql-picker-options {
				font-size: 14px !important;
			}
		}
	`}
`

export default RTEComponent
